/**
 * ResultGrid component provides a reusable data grid for displaying various types of results.
 * This component encapsulates the common data grid functionality used across different result views.
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import DataGrid, {
  Column,
  GroupPanel,
  Scrolling,
  SearchPanel,
  Export,
  Paging,
  LoadPanel,
  Pager,
  FilterRow,
} from "devextreme-react/data-grid";
import { Row, Col } from "react-bootstrap";
import { SelectBox } from "devextreme-react/select-box";
import PyramidCard from "components/common/pyramid/PyramidCard";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import saveAs from "file-saver";

/**
 * ResultGrid component for displaying and interacting with result data in a standardized way.
 *
 * @param {Object} props - Component props
 * @param {Array} props.items - List of items to choose from (computations or variation analyses)
 * @param {string} props.selectedItemId - Currently selected item ID
 * @param {function} props.onItemChange - Handler for when the selected item changes
 * @param {boolean} props.isLoading - Loading state for initial data fetch
 * @param {boolean} props.isLoadingData - Loading state for grid data
 * @param {Array} props.gridData - Data to be displayed in the grid
 * @param {Object} props.headers - Headers configuration for the grid
 * @param {Object} props.expandedHeaders - Tracks which header groups are expanded
 * @param {function} props.toggleHeaderExpansion - Function to toggle header expansion
 * @param {function} props.setExpandedHeaders - Optional function to set all expanded headers at once
 * @param {Object} props.formatMap - Map of fieldName -> {format, dataType} for converted formats
 * @param {string} props.keyExprField - Field name to use as key in the data grid
 * @param {string} props.itemSelectLabel - Label for the item selection dropdown
 */
const ResultGrid = ({
  items,
  groupedItems,
  selectedItemId,
  onItemChange,
  isLoading,
  isLoadingData,
  gridData,
  headers,
  expandedHeaders,
  toggleHeaderExpansion,
  setExpandedHeaders,
  formatMap = {},
  keyExprField,
  itemSelectLabel = "Choose your computation",
  resultType,
  toDate,
  description,
  grouped = false,
  moduleName = "Module",
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const containerRef = useRef(null);

  const toggleFullScreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) return document.exitFullscreen?.();
    container.requestFullscreen?.();
  }, []);

  const filterFullScreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    document.querySelectorAll(".dx-overlay-wrapper").forEach((overlay) => {
      if (!container.contains(overlay)) container.appendChild(overlay);
    });
  }, []);

  useEffect(() => {
    let observer = null;
    const handleFullscreenChange = () => {
      document.body.style.overflow = document.fullscreenElement ? "hidden" : "";
      if (document.fullscreenElement) {
        observer = new MutationObserver(filterFullScreen);
        observer.observe(document.body, { childList: true });
        filterFullScreen();
      } else {
        observer?.disconnect();
        observer = null;
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.body.style.overflow = "";
      observer?.disconnect?.();
    };
  }, [filterFullScreen]);

  /**
   * Determines if all expandable headers are currently expanded
   * @returns {boolean} - True if all expandable headers are expanded
   */
  const areAllExpanded = useMemo(() => {
    const expandableHeaders = Object.entries(headers).filter(
      ([, groupData]) => groupData.canBeExpanded
    );
    if (expandableHeaders.length === 0) return false;
    return expandableHeaders.every(
      ([parentCaption]) => expandedHeaders[parentCaption] === true
    );
  }, [headers, expandedHeaders]);

  /**
   * Handles expand/collapse all functionality
   * Expands or collapses all headers that can be expanded
   */
  const handleExpandCollapseAll = useCallback(() => {
    const expandableHeaders = Object.keys(headers).filter(
      (key) => headers[key].canBeExpanded
    );

    const targetState = !areAllExpanded;

    // If setExpandedHeaders is provided, use it for a single batch update
    if (setExpandedHeaders) {
      const newExpandedState = { ...expandedHeaders };
      expandableHeaders.forEach((parentCaption) => {
        newExpandedState[parentCaption] = targetState;
      });
      setExpandedHeaders(newExpandedState);
    } else {
      // Fallback: toggle each header individually
      expandableHeaders.forEach((parentCaption) => {
        if (expandedHeaders[parentCaption] !== targetState) {
          toggleHeaderExpansion(parentCaption);
        }
      });
    }
  }, [
    headers,
    expandedHeaders,
    areAllExpanded,
    toggleHeaderExpansion,
    setExpandedHeaders,
  ]);

  /**
   * Toolbar items configuration for expand/collapse all button
   */
  const toolbarItems = useMemo(
    () => [
      {
        widget: "dxButton",
        options: {
          icon: areAllExpanded ? "hidepanel" : "showpanel",
          hint: areAllExpanded ? "Collapse all" : "Expand all",
          onClick: handleExpandCollapseAll,
        },
        location: "after",
      },
      {
        widget: "dxButton",
        options: {
          icon: "fullscreen",
          hint: "Fullscreen",
          onClick: toggleFullScreen,
        },
        location: "after",
      },
    ],
    [areAllExpanded, handleExpandCollapseAll, toggleFullScreen]
  );

  /**
   * Handles the toolbar preparation event
   * Adds custom buttons to the DataGrid toolbar
   * @param {Object} e - Event object
   */
  const onToolbarPreparing = useCallback(
    (e) => {
      // Only add the button if there are expandable headers
      const hasExpandableHeaders = Object.values(headers).some(
        (groupData) => groupData.canBeExpanded
      );

      const fullscreenButton = toolbarItems[1];
      e.toolbarOptions.items.unshift(fullscreenButton);

      if (hasExpandableHeaders) {
        const expandCollapseButton = toolbarItems[0];
        e.toolbarOptions.items.unshift(expandCollapseButton);
      }
    },
    [headers, toolbarItems]
  );

  /**
   * Handles the grid's content ready event
   * Used to automatically adjust column widths
   * @param {Object} e - The event object
   */
  const onContentReady = (e) => {
    // Automatically adjust columns to fit their content
    e.component.option("columnAutoWidth", true);
  };

  /**
   * Custom render function for standard column headers
   * @param {Object} data - Header cell data
   * @returns {JSX.Element} - Rendered header cell
   */
  const headerCellRender = (data) => {
    return <div className="result-header" style={{ textAlign: 'center' }}>{data.column.caption}</div>;
  };

  /**
   * Render function for group headers with expand/collapse functionality
   * @param {string} parentCaption - The caption of the parent header
   * @param {Object} groupData - Data about the header group
   * @returns {JSX.Element} - Rendered group header
   */
  const renderGroupHeader = (parentCaption, groupData) => {
    return (
      <div className="result-group-header">
        <span>{parentCaption}</span>
        {groupData.canBeExpanded && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleHeaderExpansion(parentCaption);
            }}
            className="result-expand-button"
          >
            {expandedHeaders[parentCaption] ? (
              <i className="bi bi-arrow-bar-left" aria-label="collapse"></i>
            ) : (
              <i className="bi bi-arrow-bar-right" aria-label="expand"></i>
            )}
          </button>
        )}
      </div>
    );
  };

  /**
   * Handles export to Excel functionality
   * @param {Object} e - Export event object
   */
  const onExporting = (e) => {
    setIsExporting(true);
    e.component.beginUpdate();
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Results");

    exportDataGrid({
      component: e.component,
      worksheet: worksheet,
      autoFilterEnabled: true,
      loadPanel: {
        enabled: false,
      },
    })
      .then(function () {
        return workbook.xlsx.writeBuffer();
      })
      .then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          moduleName +
          "-" +
          resultType +
          "_Results_" +
          description +
          toDate +
          ".xlsx"
        );
        setIsExporting(false);
        e.component.endUpdate();
      })
      .catch(function (error) {
        console.error("Export error:", error);
        setIsExporting(false);
        e.component.endUpdate();
      });
  };

  return (
    <div>
      {/* Header Row - Item Selector */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <PyramidCard className="h-100">
            <div className="d-flex flex-column h-100 py-2 justify-content-around">
              <div className="fw-medium fs-7 text-nowrap mt-n2 pb-2">
                {itemSelectLabel}
              </div>
              <SelectBox
                dataSource={grouped ? groupedItems : items}
                displayExpr="description"
                valueExpr="id"
                grouped={grouped}
                value={selectedItemId}
                onValueChanged={(e) => onItemChange(e.value)}
                disabled={isLoading}
                searchEnabled={true}
                placeholder={itemSelectLabel}
                className="w-100"
              />
            </div>
          </PyramidCard>
        </Col>
      </Row>

      {/* Data Grid Row */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <PyramidCard>
            <div
              className="position-relative result-grid-container"
              ref={containerRef}
            >
              {isLoadingData && (
                <div className="loading-overlay">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {isExporting && (
                <div className="loading-overlay d-flex flex-column align-items-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Exporting data ...</span>
                  </div>
                  <div className="text-center mt-3 fw-semibold">
                    Exporting data...
                  </div>
                </div>
              )}

              <DataGrid
                dataSource={gridData}
                showBorders={true}
                showColumnLines={true}
                showRowLines={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                rowAlternationEnabled={true}
                columnHidingEnabled={false}
                wordWrapEnabled={false}
                columnAutoWidth={true}
                height="100%"
                keyExpr={keyExprField}
                noDataText=""
                onContentReady={onContentReady}
                columnResizingMode="widget"
                columnMinWidth={120}
                onExporting={onExporting}
                onToolbarPreparing={onToolbarPreparing}
              >
                <FilterRow visible={true} applyFilter="auto" />
                <Scrolling mode="virtual" useNative={true} />
                <GroupPanel visible={true} />
                <SearchPanel visible={true} highlightCaseSensitive={true} />
                <Export enabled={true} />
                <LoadPanel indicatorSrc="/assets/img/bootstrap-spinner.svg" />
                <Paging defaultPageSize={100} />
                <Pager
                  visible={true}
                  allowedPageSizes={[10, 20, 50, 100, 200]}
                  displayMode="full"
                  showPageSizeSelector={true}
                  showNavigationButtons={true}
                  showInfo={true}
                />
                {/* Ordering groups by rank before rendering */}
                {Object.entries(headers)
                  .sort(([, groupDataA], [, groupDataB]) => {
                    const rankA = groupDataA.rank || 0;
                    const rankB = groupDataB.rank || 0;
                    return rankA - rankB;
                  })
                  .map(([parentCaption, groupData]) => (
                    <Column
                      caption={parentCaption}
                      key={parentCaption}
                      headerCellRender={() =>
                        renderGroupHeader(parentCaption, groupData)
                      }
                      allowResizing={true}
                    >
                      {groupData.headers.map((header) => {
                        // Get converted format from formatMap if available
                        const convertedFormat = formatMap[header.fieldName];
                        const columnFormat =
                          convertedFormat?.format ||
                          header.valueFormat ||
                          undefined;
                        const columnDataType =
                          convertedFormat?.dataType || undefined;
                        const isNumericLike =
                          columnDataType === "number" ||
                          columnDataType === "date";

                        return (
                          <Column
                            key={header.fieldName}
                            dataField={header.fieldName}
                            caption={header.caption}
                            visible={
                              header.isVisible || expandedHeaders[parentCaption]
                            }
                            allowFiltering={true}
                            allowSorting={true}
                            allowResizing={true}
                            minWidth={120}
                            width="auto"
                            format={columnFormat}
                            dataType={columnDataType}
                            headerCellRender={headerCellRender}
                            lookup={header.lookup}
                            cellRender={
                              isNumericLike
                                ? (cellInfo) => (
                                  <div className="rg-monospace">
                                    {cellInfo.text}
                                  </div>
                                )
                                : undefined
                            }
                          />
                        );
                      })}
                    </Column>
                  ))}
              </DataGrid>
            </div>
          </PyramidCard>
        </Col>
      </Row>
    </div>
  );
};

export default ResultGrid;
